import ClockInReminderToggle from "@/components/ClockInReminderToggle";
import InstallAppButton from "@/components/InstallAppButton";
import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { useChat } from "@/context/ChatContext";
import {
    LayoutDashboard,
    Users,
    Building2,
    CalendarDays,
    CalendarCheck,
    Wallet,
    HandCoins,
    Receipt,
    Award,
    FileBarChart,
    FileText,
    Globe,
    UserCog,
    Settings as SettingsIcon,
    Database,
    History,
    Inbox,
    MapPin,
    Crosshair,
    BookOpen,
    Mail,
    Repeat,
    ListTodo,
    Moon,
    Sun,
    LogOut,
    BadgeDollarSign,
    CreditCard,
    MessagesSquare,
    UserCheck,
    Sparkles,
} from "lucide-react";
import { hasRole, isFinanceLead, userRoles, ROLE_LABELS, isRestricted, scopedPages } from "@/lib/roles";

const ALL_NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", show: () => true },
    { to: "/chat", label: "Chat", icon: MessagesSquare, testid: "nav-chat", badge: "chat", show: () => true },
    { to: "/ai", label: "AI Assistant", icon: Sparkles, testid: "nav-ai", show: () => true },
    { to: "/check-in", label: "Check in / out", icon: Crosshair, testid: "nav-check-in",
      show: (u) => hasRole(u, "employee") },
    { to: "/my-documents", label: "My documents", icon: FileText, testid: "nav-my-documents",
      show: (u) => hasRole(u, "employee") },
    { to: "/pending-approvals", label: "Pending Approvals", icon: Inbox, testid: "nav-pending-approvals",
      show: (u) => hasRole(u, "admin", "hr", "accountant", "finance_lead", "department_lead"), badge: "pending_approvals" },
    { to: "/pay-change-requests", label: "Pay changes", icon: BadgeDollarSign, testid: "nav-pay-change-requests",
      show: (u) => hasRole(u, "admin", "hr"), badge: "pay_change_requests" },
    { to: "/employees", label: "Employees", icon: Users, testid: "nav-employees",
      show: (u) => u?.role !== "employee" },
    { to: "/employee-approvals", label: "Clerk approvals", icon: UserCheck, testid: "nav-employee-approvals",
      show: (u) => hasRole(u, "admin", "hr"), badge: "employee_changes" },
    { to: "/departments", label: "Departments", icon: Building2, testid: "nav-departments",
      show: (u) => u?.role !== "employee" },
    { to: "/leaves", label: "Leave Requests", icon: CalendarDays, testid: "nav-leaves", show: () => true },
    { to: "/attendance", label: "Attendance", icon: CalendarCheck, testid: "nav-attendance",
      show: (u) => hasRole(u, "admin", "hr", "department_lead") },
    { to: "/tasks", label: "Tasks", icon: ListTodo, testid: "nav-tasks",
      show: (u) => hasRole(u, "admin", "hr", "department_lead", "employee") },
    { to: "/offsite", label: "Offsite", icon: MapPin, testid: "nav-offsite",
      show: (u) => hasRole(u, "admin", "hr", "department_lead", "employee") },
    { to: "/worksites", label: "Worksites", icon: MapPin, testid: "nav-worksites",
      show: (u) => hasRole(u, "admin", "hr") },
    { to: "/loans", label: "Loans / Advances", icon: HandCoins, testid: "nav-loans", show: () => true },
    { to: "/recurring-deductions", label: "Recurring Deductions", icon: Repeat, testid: "nav-recurring-deductions",
      show: (u) => hasRole(u, "admin", "hr") },
    { to: "/my/requests", label: "My Requests", icon: Inbox, testid: "nav-my-requests",
      show: (u) => hasRole(u, "employee"), badge: "my_requests" },
    { to: "/my/performance", label: "My Performance", icon: Award, testid: "nav-my-performance",
      show: (u) => hasRole(u, "employee") },
    { to: "/my/payslips", label: "My Payslips", icon: Wallet, testid: "nav-my-payslips",
      show: (u) => hasRole(u, "employee") },
    { to: "/my/loan-ledger", label: "My Loan Ledger", icon: BookOpen, testid: "nav-my-loan-ledger",
      show: (u) => hasRole(u, "employee") },
    { to: "/reports/payslips", label: "Payslip Report", icon: Wallet, testid: "nav-payslip-report",
      show: (u) => hasRole(u, "admin", "hr", "accountant") || isFinanceLead(u) },
    { to: "/reports/loan-ledger", label: "Loan Ledger Report", icon: BookOpen, testid: "nav-loan-ledger-report",
      show: (u) => hasRole(u, "admin", "hr", "accountant") || isFinanceLead(u) },
    { to: "/settlements", label: "F&F Settlements", icon: Receipt, testid: "nav-settlements",
      show: (u) => hasRole(u, "admin", "hr", "accountant", "department_lead") },
    { to: "/performance", label: "Performance", icon: Award, testid: "nav-performance",
      show: (u) => hasRole(u, "admin", "hr", "department_lead") },
    { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents",
      show: (u) => hasRole(u, "admin", "hr") },
    { to: "/memos", label: "AI Email Drafts", icon: Mail, testid: "nav-memos",
      show: (u) => hasRole(u, "admin", "hr") },
    { to: "/payroll", label: "Payroll", icon: Wallet, testid: "nav-payroll",
      show: (u) => hasRole(u, "admin", "hr", "accountant") || isFinanceLead(u) },
    { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports",
      show: (u) => hasRole(u, "admin", "hr", "accountant") || isFinanceLead(u) },
    { to: "/users", label: "Admin Users", icon: UserCog, testid: "nav-users",
      show: (u) => hasRole(u, "admin") },
    { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing",
      show: (u) => hasRole(u, "admin") },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings",
      show: (u) => hasRole(u, "admin") },
    { to: "/backups", label: "Backups", icon: Database, testid: "nav-backups",
      show: (u) => hasRole(u, "admin") },
    { to: "/activity-log", label: "Activity log", icon: History, testid: "nav-activity-log",
      show: (u) => hasRole(u, "admin") },
];

export default function Sidebar({ onNavigate }) {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const chatUnread = useChat()?.unread || 0;
    const navigate = useNavigate();
    const [badges, setBadges] = useState({ my_requests: 0, pending_approvals: 0 });
    const [company, setCompany] = useState({ name: "", logo_data_url: "" });

    useEffect(() => {
        if (!user) return;
        let alive = true;
        const fetchBadges = async () => {
            try {
                const { data } = await api.get("/sidebar/badges");
                if (alive) setBadges(data || { my_requests: 0, pending_approvals: 0 });
            } catch {/* ignore */}
        };
        const fetchCompany = async () => {
            try {
                const { data } = await api.get("/company-settings");
                if (alive) setCompany({ name: data?.name || "", logo_data_url: data?.logo_data_url || "" });
            } catch {/* ignore */}
        };
        fetchBadges(); fetchCompany();
        const t = setInterval(fetchBadges, 60000);
        window.addEventListener("badges:refresh", fetchBadges);
        return () => { alive = false; clearInterval(t); window.removeEventListener("badges:refresh", fetchBadges); };
    }, [user]);

    const onLogout = async () => {
        await logout();
        onNavigate?.();
        navigate("/login", { replace: true });
    };

    let nav = isRestricted(user) ? ALL_NAV.filter((n) => scopedPages(user).includes(n.to)) : ALL_NAV.filter((n) => n.show(user));
    // Employee role: inject a "My profile" link to their own employee record
    if (hasRole(user, "employee") && user?.employee_id) {
        nav = [
            nav[0], // Dashboard
            ...nav.slice(1).filter((n) => n.testid !== "nav-my-profile"),
            { to: `/employees/${user.employee_id}`, label: "My profile", icon: Users, testid: "nav-my-profile" },
        ];
        // dedupe just in case
        const seen = new Set();
        nav = nav.filter((n) => (seen.has(n.testid) ? false : seen.add(n.testid)));
    }

    const roleBadge = userRoles(user)
        .map((r) => (r === "department_lead" && isFinanceLead(user) ? "Finance Lead" : ROLE_LABELS[r] || r))
        .filter((v, i, a) => a.indexOf(v) === i)
        .join(" · ");

    const initials = (user?.name || user?.email || "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

    return (
        <aside
            className="w-72 lg:w-64 shrink-0 h-screen sticky top-0 flex flex-col overflow-hidden bg-[#0E1630] text-white"
            data-testid="sidebar"
        >
            <div className="shrink-0 px-5 pt-6 pb-5 border-b border-white/10" data-testid="sidebar-brand">
                <div className="flex items-center gap-3">
                    {company.logo_data_url ? (
                        <img src={company.logo_data_url} alt={company.name || "logo"} className="w-10 h-10 object-contain bg-white rounded-xl p-1" />
                    ) : (
                        <div className="w-10 h-10 rounded-xl bg-klein flex items-center justify-center">
                            <span className="font-display font-extrabold text-white text-lg">{(company.name || "H")[0].toUpperCase()}</span>
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="font-display font-bold text-white text-[15px] leading-tight line-clamp-2" data-testid="sidebar-company-name">
                            {company.name || "HR/M"}
                        </div>
                        <div className="mt-0.5 text-[11px] text-white/50">People workspace</div>
                    </div>
                </div>
                {userRoles(user).some((r) => r !== "admin") && (
                    <div className="mt-4 inline-flex items-center rounded-full bg-white/10 text-white/80 text-[10px] font-semibold uppercase tracking-[0.14em] px-2.5 py-1" data-testid="sidebar-role-badge">
                        {roleBadge}
                    </div>
                )}
            </div>

            <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-0.5 sidebar-scroll" data-testid="sidebar-scroll">
                {nav.map((n) => {
                    const Icon = n.icon;
                    const count = n.badge === "chat" ? chatUnread : n.badge ? badges[n.badge] : 0;
                    return (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={n.testid}
                            onClick={() => onNavigate?.()}
                            className={({ isActive }) =>
                                `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] transition-colors ${
                                    isActive
                                        ? "bg-white/10 text-white font-semibold before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:rounded-full before:bg-terracotta"
                                        : "text-white/65 hover:text-white hover:bg-white/5"
                                }`
                            }
                        >
                            <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                            <span className="flex-1">{n.label}</span>
                            {n.badge && count > 0 && (
                                <span
                                    data-testid={`${n.testid}-badge`}
                                    className="ml-auto inline-flex items-center justify-center min-w-[1.4rem] h-5 px-1.5 bg-terracotta text-white text-[10px] font-bold rounded-full"
                                >
                                    {count > 99 ? "99+" : count}
                                </span>
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            <div className="shrink-0 border-t border-white/10 p-4" data-testid="sidebar-footer">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-terracotta to-gold flex items-center justify-center text-xs font-bold text-white">
                        {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[11px] text-white/50">Signed in as</div>
                        <div className="text-sm font-medium text-white truncate" data-testid="current-user-email">{user?.email}</div>
                    </div>
                </div>
                <div className="mt-3 flex items-stretch gap-2">
                    <button
                        onClick={onLogout}
                        data-testid="logout-button"
                        className="flex-1 flex items-center gap-2 justify-center rounded-xl bg-white/10 text-white px-4 py-2.5 text-sm hover:bg-white/20 transition-colors"
                    >
                        <LogOut className="w-4 h-4" strokeWidth={1.75} />
                        Sign out
                    </button>
                    <button
                        onClick={toggle}
                        data-testid="theme-toggle"
                        aria-label="Toggle dark mode"
                        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                        className="w-11 flex items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
                    >
                        {theme === "dark" ? <Sun className="w-4 h-4" strokeWidth={1.75} /> : <Moon className="w-4 h-4" strokeWidth={1.75} />}
                    </button>
                </div>
                <InstallAppButton className="mt-2 w-full" />
                {user?.employee_id && <ClockInReminderToggle className="mt-2 w-full" />}
            </div>
        </aside>
    );
}
